if (!customElements.get('early-access-gate')) {
  const SUBSCRIBE_INTENT_KEY = 'early-access-subscribe-intent';
  // Query params accepted to prefill the code input, e.g. from a newsletter
  // link: /collections/xxx?code=ABC-1234. First one present wins.
  const CODE_PARAM_KEYS = ['code', 'ea_code', 'early_access_code'];
  // Same thing, but validated on load without any click: used as the
  // redirect URL of an external signup form (Meta lead ads), where the
  // visitor has already given their email before landing here.
  const AUTO_CODE_PARAM_KEYS = ['access', 'ea_access'];

  function readParam(params, keys) {
    const key = keys.find((name) => (params.get(name) || '').trim() !== '');
    return key ? params.get(key).trim() : null;
  }

  customElements.define(
    'early-access-gate',
    class EarlyAccessGate extends HTMLElement {
      connectedCallback() {
        this.codeForm = this.querySelector('[data-gate-code-form]');
        this.codeError = this.querySelector('[data-gate-code-error]');
        if (this.codeForm) this.codeForm.addEventListener('submit', this.onCodeSubmit.bind(this));

        this.querySelectorAll('[data-gate-tab]').forEach((tab) => {
          tab.addEventListener('click', () => this.selectTab(tab.dataset.gateTab));
        });

        // Mark the signup as initiated from this browser before the native
        // customer form navigates away, so that a shared/revisited URL with
        // customer_posted=true does not unlock by itself.
        const subscribeForm = this.querySelector('[data-gate-subscribe-form] form');
        if (subscribeForm) {
          subscribeForm.addEventListener('submit', () => {
            // The native POST navigates away: the spinner stays on until the
            // redirect back to the collection replaces the page.
            const button = subscribeForm.querySelector('button[type="submit"]');
            const spinner = button && button.querySelector('.loading-overlay__spinner');
            if (button) button.classList.add('loading');
            if (spinner) spinner.classList.remove('hidden');
            try {
              sessionStorage.setItem(SUBSCRIBE_INTENT_KEY, '1');
            } catch (error) {
              // Storage unavailable: the signup itself still goes through,
              // the visitor just comes back to the locked gate.
            }
          });
        }

        // Returning from the native newsletter POST (customer_posted param set
        // via return_to): show the success state and unlock. The subscribe
        // intent flag keeps a shared/revisited URL from triggering this for a
        // browser that never submitted the form.
        const params = new URLSearchParams(window.location.search);

        if (params.get('customer_posted') === 'true' && this.hasSubscribeIntent()) {
          this.selectTab('subscribe');
          const success = this.querySelector('[data-gate-subscribe-success]');
          const formWrapper = this.querySelector('[data-gate-subscribe-form]');
          if (success) success.hidden = false;
          if (formWrapper) formWrapper.hidden = true;
          this.unlockAfterSubscribe();
        } else if (this.querySelector('[data-gate-form-error]')) {
          this.selectTab('subscribe');
        } else {
          this.applyCodeFromUrl(params);
        }
      }

      /*
       * Two kinds of deep link, both carrying the collection access code:
       *   ?code=XXX   (newsletter) fills the input, the subscriber only has
       *               to press the button. Not submitted on its own: an
       *               expired code would show an error before the visitor
       *               has done anything.
       *   ?access=XXX (redirect of an external signup form) validates right
       *               away, so a visitor who already left their email lands
       *               on the collection without a second form.
       */
      applyCodeFromUrl(params) {
        const input = this.querySelector('[data-gate-code-input]');
        if (!input || !this.codeForm) return;

        const autoCode = readParam(params, AUTO_CODE_PARAM_KEYS);
        const code = autoCode || readParam(params, CODE_PARAM_KEYS);
        if (!code) return;

        input.value = code;
        this.selectTab('code');

        const button = this.codeForm.querySelector('button[type="submit"]');

        if (!autoCode) {
          if (button) button.focus({ preventScroll: true });
          return;
        }

        // Reuse the normal submit path: same cart attribute, same
        // server-side validation, and the usual error message if the code
        // is no longer valid.
        this.setAttribute('data-auto-unlocking', '');
        if (typeof this.codeForm.requestSubmit === 'function') {
          this.codeForm.requestSubmit();
        } else {
          this.onCodeSubmit({ preventDefault() {} });
        }
      }

      hasSubscribeIntent() {
        try {
          return sessionStorage.getItem(SUBSCRIBE_INTENT_KEY) === '1';
        } catch (error) {
          return false;
        }
      }

      /*
       * Newsletter signup unlocks the page directly (no code emailed): store
       * the subscription as a hidden cart attribute — read server-side by the
       * same Liquid checks as the code — then reload the collection without
       * the customer_posted param. On failure the success message stays
       * visible and the page simply remains locked.
       */
      async unlockAfterSubscribe() {
        const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

        try {
          const response = await fetch(`${rootUrl}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: { _early_access_subscribed: 'true' } }),
          });
          if (!response.ok) return;

          try {
            sessionStorage.removeItem(SUBSCRIBE_INTENT_KEY);
          } catch (error) {
            // Ignore: the attribute is set, a stale flag is harmless.
          }
          window.location.replace(this.dataset.collectionUrl);
        } catch (error) {
          // Network failure: keep the gate as is.
        }
      }

      selectTab(name) {
        this.querySelectorAll('[data-gate-tab]').forEach((tab) => {
          tab.setAttribute('aria-selected', tab.dataset.gateTab === name ? 'true' : 'false');
        });
        this.querySelectorAll('[data-gate-panel]').forEach((panel) => {
          panel.hidden = panel.dataset.gatePanel !== name;
        });
      }

      /*
       * The entered code is stored as a hidden cart attribute, then validated
       * server-side by re-fetching this section through the Section Rendering
       * API: Liquid compares the attribute to the collection metafield and
       * exposes the result in [data-early-access-state].
       */
      async onCodeSubmit(event) {
        event.preventDefault();
        const input = this.querySelector('[data-gate-code-input]');
        const code = (input.value || '').trim();
        if (!code) return;

        const button = this.codeForm.querySelector('button[type="submit"]');
        const spinner = button.querySelector('.loading-overlay__spinner');
        button.classList.add('loading');
        spinner.classList.remove('hidden');
        this.codeError.hidden = true;

        const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

        try {
          const updateResponse = await fetch(`${rootUrl}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: { _early_access_code: code } }),
          });

          // Cart validation apps can reject any cart update (e.g. shipping
          // restrictions): surface their message instead of "wrong code".
          if (!updateResponse.ok) {
            const updateBody = await updateResponse.json().catch(() => ({}));
            this.showError(updateBody.message || updateBody.description);
            return;
          }

          const response = await fetch(
            `${this.dataset.collectionUrl}?section_id=${encodeURIComponent(this.dataset.sectionId)}`
          );
          const html = await response.text();

          if (html.includes('data-early-access-state="unlocked"')) {
            window.location.href = this.dataset.collectionUrl;
            return;
          }

          this.showError();
          input.focus();
        } catch (error) {
          this.showError();
        } finally {
          button.classList.remove('loading');
          spinner.classList.add('hidden');
          this.removeAttribute('data-auto-unlocking');
        }
      }

      showError(message) {
        this.codeError.textContent = message || this.codeError.dataset.defaultMessage;
        this.codeError.hidden = false;
      }
    }
  );
}
